import * as React from "react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { ChevronDown } from "lucide-react";

// Simple className join helper
function cx(...parts: Array<string | false | null | undefined>) {
    return parts.filter(Boolean).join(" ");
}

export type DropdownVariant = "primary" | "secondary" | "success" | "danger" | "warning" | "info" | "light" | "dark" | "link";
export type DropdownSize = "xs" | "sm" | "lg";
export type DropdownAlign = "start" | "end" | "center";

// Main Dropdown component props
export interface DropdownProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "onSelect" | "onToggle"> {
    /** Matches react-bootstrap's `show` (controlled). If undefined, component is uncontrolled. */
    show?: boolean;
    /** Matches react-bootstrap's `onToggle` */
    onToggle?: (isOpen: boolean, event?: Event) => void;
    /** Matches react-bootstrap's `drop` - direction of dropdown */
    drop?: "up" | "down" | "start" | "end";
    /** Matches react-bootstrap's `align` */
    align?: DropdownAlign;
    /** Matches react-bootstrap's `autoClose` */
    autoClose?: boolean | "outside" | "inside";
}

// Dropdown.Toggle props
export interface DropdownToggleProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "onClick"> {
    /** Matches react-bootstrap's `variant` */
    variant?: DropdownVariant;
    /** Matches react-bootstrap's `size` */
    size?: DropdownSize;
    /** Matches react-bootstrap's `as` - render as different element */
    as?: React.ElementType;
    /** Matches react-bootstrap's `bsPrefix` - custom class prefix */
    bsPrefix?: string;
    /** Matches react-bootstrap's `split` */
    split?: boolean;
    /** Matches react-bootstrap's `childBsPrefix` */
    childBsPrefix?: string;
}

// Dropdown.Menu props
export interface DropdownMenuProps extends React.HTMLAttributes<HTMLDivElement> {
    /** Matches react-bootstrap's `align` */
    align?: DropdownAlign;
    /** Matches react-bootstrap's `flip` */
    flip?: boolean;
    /** Matches react-bootstrap's `popperConfig` */
    popperConfig?: unknown;
}

// Dropdown.Item props
export interface DropdownItemProps extends Omit<React.HTMLAttributes<HTMLElement>, "onClick" | "onSelect"> {
    /** Matches react-bootstrap's `as` - render as different element */
    as?: React.ElementType;
    /** Matches react-bootstrap's `active` */
    active?: boolean;
    /** Matches react-bootstrap's `disabled` */
    disabled?: boolean;
    /** Matches react-bootstrap's `eventKey` */
    eventKey?: string | number;
    /** Matches react-bootstrap's `onClick` */
    onClick?: (event: React.MouseEvent<HTMLElement>) => void;
    /** Matches react-bootstrap's `onSelect` */
    onSelect?: (eventKey: string | number | null, event: React.SyntheticEvent<HTMLElement>) => void;
}

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface DropdownHeaderProps extends React.HTMLAttributes<HTMLDivElement> {}

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface DropdownDividerProps extends React.HTMLAttributes<HTMLHRElement> {}

// Variant styles for Toggle button
const VARIANT_STYLES: Record<DropdownVariant, string> = {
    primary: "bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500",
    secondary: "bg-gray-600 text-white hover:bg-gray-700 focus:ring-gray-500",
    success: "bg-green-600 text-white hover:bg-green-700 focus:ring-green-500",
    danger: "bg-red-500 text-white hover:bg-red-700 focus:ring-red-500",
    warning: "bg-yellow-500 text-white hover:bg-yellow-600 focus:ring-yellow-500",
    info: "bg-cyan-600 text-white hover:bg-cyan-700 focus:ring-cyan-500",
    light: "bg-white text-gray-900 border border-gray-300 hover:bg-gray-50 focus:ring-gray-500",
    dark: "bg-gray-800 text-white hover:bg-gray-900 focus:ring-gray-700",
    link: "text-blue-600 hover:text-blue-800 underline focus:ring-blue-500 bg-transparent border-0"
};

const SIZE_STYLES: Record<DropdownSize, string> = {
    xs: "text-xs",
    sm: "px-2 py-1 text-sm",
    lg: "px-4 py-2 text-lg"
};

const DEFAULT_TOGGLE_STYLES = "px-3 py-2 text-base";

// Context to pass dropdown state to children
const DropdownContext = React.createContext<{
    show: boolean;
    onOpenChange: (isOpen: boolean) => void;
    side: "top" | "bottom" | "left" | "right";
    align: "start" | "end" | "center";
}>({
    show: false,
    onOpenChange: () => {},
    side: "bottom",
    align: "start"
});

/**
 * Drop-in replacement for react-bootstrap's <Dropdown> using Radix UI primitives
 * - Accessible by default
 * - Supports controlled/uncontrolled `show`
 * - Subcomponents: <Dropdown.Toggle>, <Dropdown.Menu>, <Dropdown.Item>, <Dropdown.Header>, <Dropdown.Divider>
 */
export function Dropdown(props: DropdownProps) {
    const { show: showProp, onToggle, drop = "down", align = "start", autoClose = true, className, children, ...rest } = props;

    const uncontrolled = showProp === undefined;
    const [open, setOpen] = React.useState(false);
    const show = uncontrolled ? open : !!showProp;

    const handleOpenChange = (isOpen: boolean) => {
        if (onToggle) {
            onToggle(isOpen);
        }
        if (uncontrolled) {
            setOpen(isOpen);
        }
    };

    // Determine side based on drop prop
    const side = drop === "up" ? "top" : drop === "down" ? "bottom" : drop === "start" ? "left" : "right";

    // Determine align based on align prop
    const alignProp = align === "start" ? "start" : align === "end" ? "end" : "center";

    return (
        <DropdownContext.Provider value={{ show, onOpenChange: handleOpenChange, side, align: alignProp }}>
            <DropdownMenu.Root open={show} onOpenChange={handleOpenChange}>
                <div className={cx("inline-block", className)} {...rest}>
                    {children}
                </div>
            </DropdownMenu.Root>
        </DropdownContext.Provider>
    );
}

/**
 * Dropdown.Toggle subcomponent
 */
const DropdownToggle = React.forwardRef<HTMLButtonElement, DropdownToggleProps>((props, ref) => {
    const {
        variant = "primary",
        size,
        as: Component = "button",
        bsPrefix,
        split = false,
        childBsPrefix,
        className,
        children,
        ...rest
    } = props;

    const buttonClasses = cx(
        "inline-flex items-center justify-center",
        "font-medium rounded-md",
        "focus:outline-none focus:ring-2 focus:ring-offset-2",
        "transition-colors duration-200",
        "disabled:opacity-50 disabled:cursor-not-allowed",
        bsPrefix || VARIANT_STYLES[variant],
        size ? SIZE_STYLES[size] : DEFAULT_TOGGLE_STYLES,
        className
    );

    const content = (
        <>
            {children}
            {!split && <ChevronDown className="ml-1 h-4 w-4" aria-hidden="true" />}
        </>
    );

    return (
        <DropdownMenu.Trigger asChild>
            <Component ref={ref} className={buttonClasses} {...rest}>
                {content}
            </Component>
        </DropdownMenu.Trigger>
    );
});
DropdownToggle.displayName = "DropdownToggle";

/**
 * Dropdown.Menu subcomponent
 */
const DropdownMenuComponent = React.forwardRef<HTMLDivElement, DropdownMenuProps>((props, ref) => {
    const { align: alignProp, flip = true, popperConfig, className, children, ...rest } = props;
    const context = React.useContext(DropdownContext);
    const side = context.side;
    const align = alignProp ?? context.align;

    return (
        <DropdownMenu.Portal>
            <DropdownMenu.Content
                ref={ref}
                side={side}
                align={align}
                sideOffset={4}
                className={cx(
                    "min-w-[220px] bg-white rounded-md shadow-lg border border-gray-200 p-1 z-50",
                    "data-[state=open]:animate-in data-[state=closed]:animate-out",
                    "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
                    "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
                    "data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2",
                    "data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
                    className
                )}
                {...rest}
            >
                {children}
            </DropdownMenu.Content>
        </DropdownMenu.Portal>
    );
});
DropdownMenuComponent.displayName = "DropdownMenu";

/**
 * Dropdown.Item subcomponent
 */
const DropdownItem = React.forwardRef<HTMLElement, DropdownItemProps>((props, ref) => {
    const { as: Component, active = false, disabled = false, eventKey, onClick, onSelect, className, children, ...rest } = props;

    const handleSelect = (event: Event) => {
        if (disabled) {
            event.preventDefault();
            return;
        }

        const syntheticEvent = event as unknown as React.SyntheticEvent<HTMLElement>;
        if (onSelect) {
            onSelect(eventKey ?? null, syntheticEvent);
        }
        if (onClick) {
            onClick(syntheticEvent as unknown as React.MouseEvent<HTMLElement>);
        }
    };

    const itemClasses = cx(
        "w-full flex items-center px-2 py-1.5 text-sm rounded-sm",
        "cursor-pointer select-none outline-none",
        "hover:bg-gray-100 focus:bg-gray-100",
        active && "bg-blue-50 text-blue-900",
        disabled && "opacity-50 cursor-not-allowed pointer-events-none",
        className
    );

    // If Component is specified and not "div", wrap in that component
    if (Component && Component !== "div") {
        return (
            <DropdownMenu.Item asChild disabled={disabled} onSelect={handleSelect} className={itemClasses}>
                <Component ref={ref as any} {...rest}>
                    {children}
                </Component>
            </DropdownMenu.Item>
        );
    }

    // Default: render as div inside Radix Item
    return (
        <DropdownMenu.Item disabled={disabled} onSelect={handleSelect} className={itemClasses} {...rest}>
            {children}
        </DropdownMenu.Item>
    );
});
DropdownItem.displayName = "DropdownItem";

/**
 * Dropdown.Header subcomponent
 */
const DropdownHeader = React.forwardRef<HTMLDivElement, DropdownHeaderProps>((props, ref) => {
    const { className, children, ...rest } = props;

    return (
        <DropdownMenu.Label
            ref={ref}
            className={cx("px-2 py-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wider", className)}
            {...rest}
        >
            {children}
        </DropdownMenu.Label>
    );
});
DropdownHeader.displayName = "DropdownHeader";

/**
 * Dropdown.Divider subcomponent
 */
const DropdownDivider = React.forwardRef<HTMLHRElement, DropdownDividerProps>((props, ref) => {
    const { className, ...rest } = props;

    return <DropdownMenu.Separator ref={ref} className={cx("h-px bg-gray-200 my-1", className)} {...rest} />;
});
DropdownDivider.displayName = "DropdownDivider";

// Attach subcomponents
Dropdown.Toggle = DropdownToggle;
Dropdown.Menu = DropdownMenuComponent;
Dropdown.Item = DropdownItem;
Dropdown.Header = DropdownHeader;
Dropdown.Divider = DropdownDivider;
